import { Tooltip } from "@material-ui/core";
import { AddOutlined, DeleteOutline as DeleteOutlineIcon } from "@material-ui/icons";
import { useMemo } from "react";
import { isSingleOperationTaken } from "../../../constants";
import { fmt3 } from "../../../utils";

const InsightsTable = ({
    data = [],
    onAddRow,
    onDeleteRow,
    onPortChange,
    isReadOnly = false,
}) => {
    const finalData = useMemo(() => {
        if (!data?.cargoList?.length) return [];

        return data.cargoList.map((cd, index) => {
            if (index === 0) {
                return {
                    port: cd?.port || "",
                    operation: cd?.operation || "",
                };
            }

            return {
                ...cd,
                ...(data.travelDetails?.[index - 1] || {}),
                port: cd?.port || "",
                operation: cd?.operation || "",
            };
        });
    }, [data]);

    const formatNum = (val) => {
        const num = Number(val);
        if (isNaN(num)) return "0";
        return fmt3(num);
    };

    return (
        <div className="w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden border-none rounded-none bg-white scroll-thin pb-0 shadow-none">
            <table className="w-full min-w-full border-collapse text-[11px]">
                <thead>
                    {/* Top Group Header Row */}
                    <tr className="bg-[#313C4C] text-white h-[24px]">
                        {!isReadOnly && (
                            <th className="w-[24px] min-w-[24px] border-r border-b border-[#5b6779] p-0 align-middle"></th>
                        )}
                        <th colSpan="2" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
                            Itinerary
                        </th>
                        <th colSpan="4" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
                            Total Travel Days
                        </th>
                        <th colSpan="3" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
                            Total Travel Consumption
                        </th>
                        <th colSpan="4" className={`${!isReadOnly ? 'border-r' : ''} border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize`}>
                            Stay Total Consumption
                        </th>
                        {!isReadOnly && (
                            <th className="w-[24px] min-w-[24px] border-b border-[#5b6779] p-0 align-middle"></th>
                        )}
                    </tr>

                    {/* Sub Header Row */}
                    <tr className="bg-[#313C4C] text-white h-[24px]">
                        {!isReadOnly && (
                            <th className="w-[24px] min-w-[24px] text-center border-r border-b border-[#5b6779] px-0.5 py-0.5 align-middle font-medium text-[11px] h-[24px]">
                                <span className="sr-only">Add</span>
                            </th>
                        )}
                        <th className="px-1.5 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[105px] min-w-[105px]">
                            Operation
                        </th>
                        <th className="px-1.5 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[105px] min-w-[105px]">
                            Port
                        </th>
                        <Tooltip title="Observed Distance (Normal)" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                OD (Normal)
                            </th>
                        </Tooltip>
                        <Tooltip title="Distance (ECA)" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Distance (ECA)
                            </th>
                        </Tooltip>
                        <Tooltip title="Total Distance" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Total Distance
                            </th>
                        </Tooltip>
                        <Tooltip title="Total Sea Days during travel" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Total Sea Days
                            </th>
                        </Tooltip>

                        <Tooltip title="Total Travel Consumption Main Fuel" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Main Fuel
                            </th>
                        </Tooltip>
                        <Tooltip title="Total Travel Consumption ECA" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                ECA
                            </th>
                        </Tooltip>
                        <Tooltip title="Total Travel Consumption MGO" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                MGO
                            </th>
                        </Tooltip>

                        <Tooltip title="Total Stay Days in Port" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Total Stay
                            </th>
                        </Tooltip>
                        <Tooltip title="Stay Consumption Main Fuel" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                Main Fuel
                            </th>
                        </Tooltip>
                        <Tooltip title="Stay Consumption ECA" placement="top" arrow>
                            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                                ECA
                            </th>
                        </Tooltip>
                        <Tooltip title="Stay Consumption MGO" placement="top" arrow>
                            <th className={`px-1 py-0.5 text-[11px] align-middle font-medium ${!isReadOnly ? 'border-r' : ''} border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]`}>
                                MGO
                            </th>
                        </Tooltip>

                        {!isReadOnly && (
                            <th className="w-[24px] min-w-[24px] text-center border-b border-[#5b6779] px-0.5 py-0.5 align-middle font-medium text-[11px] h-[24px]">
                                <span className="sr-only">Delete</span>
                            </th>
                        )}
                    </tr>
                </thead>

                <tbody className="bg-white divide-y-0">
                    {finalData?.map((row, index) => {
                        const opBg = (index === 0 && !isReadOnly) ? "bg-[#f1f5f9]" : "bg-[#ffffd6]";
                        const obsNorm = row.observedDistanceNormal || 0;
                        const obsEca = row.observedDistanceECA || 0;
                        const totDist = Number(obsNorm) + Number(obsEca);

                        return (
                            <tr key={`cargo-${index}`} className="h-[19px] hover:bg-amber-50/50 transition-colors">
                                {!isReadOnly && (
                                    <td className="border-r border-b border-[#cbd5e1] text-center p-0 align-middle w-[24px] h-[19px] bg-white">
                                        <Tooltip title="Add Row" placement="left" arrow>
                                            <button
                                                type="button"
                                                onClick={() => onAddRow(index)}
                                                className="w-full h-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer p-0"
                                            >
                                                <AddOutlined className="!text-[13px]" />
                                            </button>
                                        </Tooltip>
                                    </td>
                                )}

                                {/* Operation */}
                                <td className={`border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] whitespace-nowrap w-[105px] min-w-[105px] ${opBg}`}>
                                    <Tooltip title={row.operation || ""} placement="top" arrow>
                                        <select
                                            className="cargo-table-select w-full h-[19px] text-[11px] font-medium text-black bg-transparent border-none outline-none px-0.5 py-0 cursor-pointer capitalize disabled:bg-[#f1f5f9] disabled:text-black leading-[19px] text-left"
                                            value={row.operation || ""}
                                            disabled={isReadOnly || index === 0}
                                            onChange={(e) => onPortChange(index, "operation", e.target.value)}
                                        >
                                            <option value="Commence" disabled={index > 0} hidden={index > 0} className="capitalize">
                                                Commence
                                            </option>
                                            <option value="Loading" className="capitalize">Loading</option>
                                            <option value="Passing" className="capitalize">Passing</option>
                                            <option value="Delivery-out" disabled={isSingleOperationTaken(data.cargoList, "Delivery-out") && row.operation !== "Delivery-out"} className="capitalize">Delivery-out</option>
                                            <option value="Redelivery-in" disabled={isSingleOperationTaken(data.cargoList, "Redelivery-in") && row.operation !== "Redelivery-in"} className="capitalize">Redelivery-in</option>
                                            <option value="Redelivery-out" className="capitalize">Redelivery-out</option>
                                            <option value="Waiting" className="capitalize">Waiting</option>
                                            <option value="Bunkering" className="capitalize">Bunkering</option>
                                            <option value="Redelivery" className="capitalize">Redelivery</option>
                                            <option value="Relay" className="capitalize">Relay</option>
                                            <option value="Discharging" className="capitalize">Discharging</option>
                                        </select>
                                    </Tooltip>
                                </td>

                                {/* Port */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap w-[105px] min-w-[105px]">
                                    <Tooltip title={row.port || "Empty"} placement="top" arrow>
                                        <span className="truncate block w-full px-1.5 font-medium text-black capitalize leading-[19px] text-left">
                                            {row.port}
                                        </span>
                                    </Tooltip>
                                </td>

                                {/* OD (Normal) */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                    <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                        {formatNum(obsNorm)}
                                    </span>
                                </td>

                                {/* Distance (ECA) */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                    <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                        {formatNum(obsEca)}
                                    </span>
                                </td>

                                {/* Total Distance */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                    <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                        {formatNum(totDist)}
                                    </span>
                                </td>

                                {/* Total Sea Days */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                    <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                        {formatNum(row.travelDays)}
                                    </span>
                                </td>

                                {/* Travel Main Fuel / ECA / MGO */}
                                {["HSFO", "LSFO", "MGO"].map((field) => (
                                    <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                        <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                            {formatNum(row[field])}
                                        </span>
                                    </td>
                                ))}

                                {/* Total Stay */}
                                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                                    <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                        {formatNum(row.totalStay)}
                                    </span>
                                </td>

                                {/* Stay Main Fuel / ECA / MGO */}
                                {["stayHSFO", "stayLSFO", "stayMGO"].map((field, fIdx) => {
                                    const isLastField = isReadOnly && fIdx === 2;
                                    return (
                                        <td key={field} className={`${isLastField ? '' : 'border-r'} border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap`}>
                                            <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                                                {formatNum(row[field])}
                                            </span>
                                        </td>
                                    );
                                })}

                                {!isReadOnly && (
                                    <td className="border-b border-[#cbd5e1] text-center p-0 align-middle w-[24px] h-[19px] bg-white">
                                        <Tooltip title="Delete Row" placement="top" arrow>
                                            <button
                                                type="button"
                                                onClick={() => onDeleteRow(index)}
                                                disabled={data.cargoList?.length <= 1}
                                                className="w-full h-full flex items-center justify-center text-rose-500 hover:text-rose-700 disabled:text-slate-300 hover:bg-rose-50 transition-colors border-none bg-transparent cursor-pointer p-0"
                                            >
                                                <DeleteOutlineIcon className="!text-[13px]" />
                                            </button>
                                        </Tooltip>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default InsightsTable;
